import React, { useState, useEffect } from 'react';
import { theme } from '../theme';
import { getApiUrl } from '../config';
import { useNotification } from './Notification';
import { ReviewReactions as ReviewReactionsType } from '../types';
import { logger } from '../utils/logger';
import { useAuth } from '../contexts/AuthContext';
import { WHATSAPP_EMOJIS } from '../data/whatsappEmojis';
import { useResponsive } from '../hooks/useResponsive';
import { useWebSocket } from '../hooks/useWebSocket';

interface ReviewReactionsProps {
  reviewId: string;
  matchId: string; // Agregado para WebSockets
  reactions?: ReviewReactionsType;
  totalReactions?: number;
  onReactionsUpdate: (reactions: ReviewReactionsType, totalReactions: number) => void;
  isHovered?: boolean; // Recibir estado de hover desde el padre
}

export const ReviewReactions: React.FC<ReviewReactionsProps> = ({
  reviewId,
  matchId,
  reactions = {},
  totalReactions = 0,
  onReactionsUpdate,
  isHovered = false
}) => {
  const { user, token } = useAuth();
  const { showNotification } = useNotification();
  const { isMobile } = useResponsive();
  const { addReaction: wsAddReaction, removeReaction: wsRemoveReaction } = useWebSocket();
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('Caras y Emociones');
  const [searchTerm, setSearchTerm] = useState('');
  const [tooltipData, setTooltipData] = useState<{
    show: boolean;
    x: number;
    y: number;
    content: string;
  } | null>(null);

  // Limpiar búsqueda cuando cambias de categoría
  useEffect(() => {
    setSearchTerm('');
  }, [selectedCategory]);

  // Limpiar tooltip cuando cambian las reacciones
  useEffect(() => {
    setTooltipData(null);
  }, [reactions]);

  // Obtener emojis de la categoría seleccionada
  const currentEmojis = WHATSAPP_EMOJIS[selectedCategory as keyof typeof WHATSAPP_EMOJIS] || [];

  // Filtrar emojis por búsqueda (solo si hay término de búsqueda)
  const filteredEmojis = searchTerm 
    ? currentEmojis.filter(emoji => emoji.includes(searchTerm))
    : currentEmojis;

  // Búsqueda global en todos los emojis
  const searchAllEmojis = () => {
    if (!searchTerm) return [];
    const allEmojis: string[] = [];
    Object.values(WHATSAPP_EMOJIS).forEach(category => {
      allEmojis.push(...category);
    });
    
    // Buscar por emoji exacto o por descripción aproximada
    return allEmojis.filter(emoji => {
      const searchLower = searchTerm.toLowerCase();
      
      // Buscar por emoji exacto
      if (emoji.includes(searchTerm)) return true;
      
      // Buscar por descripción aproximada basada en categorías
      const category = Object.entries(WHATSAPP_EMOJIS).find(([cat, emojis]) => 
        emojis.includes(emoji)
      )?.[0];
      
      if (category && category.toLowerCase().includes(searchLower)) return true;
      
      // Búsqueda por palabras clave comunes
      const keywords: { [key: string]: string[] } = {
        // Emociones básicas
        'feliz': ['😀', '😃', '😄', '😁', '😅', '😂', '🤣', '😊', '😇', '🙂', '😉', '😍', '🥰'],
        'triste': ['😔', '😕', '😢', '😭', '😞', '😟', '😤', '😥', '😪', '😫'],
        'enojado': ['😠', '😡', '🤬', '😈', '👿', '💢', '😤'],
        'sorprendido': ['😮', '😯', '😲', '😱', '🤯', '😨', '😰', '😳'],
        'amor': ['😍', '🥰', '😘', '😗', '😙', '😚', '💕', '💞', '💓', '💗', '💖', '💘', '💝'],
        'risa': ['😀', '😃', '😄', '😁', '😅', '😂', '🤣', '😊', '😉', '😋', '😛', '😜', '😝'],
        'llanto': ['😢', '😭', '😿', '💧', '💦', '😪', '😫'],
        
        // Personas y características
        'anciano': ['👵', '🧓', '👴', '👨‍🦳', '👩‍🦳'],
        'joven': ['👶', '👧', '🧒', '👦', '👩', '🧑', '👨'],
        'mujer': ['👩', '👩‍🦱', '👩‍🦰', '👱‍♀️', '👩‍🦳', '👩‍🦲', '🧔‍♀️'],
        'hombre': ['👨', '👨‍🦱', '👨‍🦰', '👱‍♂️', '👨‍🦳', '👨‍🦲', '🧔‍♂️'],
        'bebe': ['👶', '🍼', '👶'],
        'discapacitado': ['🦽', '🦼', '👨‍🦽', '👩‍🦽', '👨‍🦼', '👩‍🦼'],
        'silla': ['🦽', '🦼'],
        'sordo': ['🧏‍♂️', '🧏‍♀️', '🧏'],
        'ciego': ['👨‍🦯', '👩‍🦯'],
        
        // Diversidad e inclusión
        'lgbt': ['🏳️‍🌈', '🌈', '🏳️‍⚧️'],
        'arcoiris': ['🌈', '🏳️‍🌈'],
        'bandera': ['🏁', '🚩', '🎌', '🏴', '🏳️', '🏳️‍🌈', '🏴‍☠️'],
        'gay': ['🏳️‍🌈', '🌈'],
        'trans': ['🏳️‍⚧️', '⚧'],
        'diversidad': ['🏳️‍🌈', '🌈', '🏳️‍⚧️', '👨‍❤️‍👨', '👩‍❤️‍👩'],
        
        // Comida específica
        'berenjena': ['🍆'],
        'pizza': ['🍕'],
        'hamburguesa': ['🍔'],
        'sushi': ['🍣'],
        'helado': ['🍦', '🍨'],
        'cafe': ['☕'],
        'cerveza': ['🍺', '🍻'],
        'vino': ['🍷', '🍸', '🍹'],
        'manzana': ['🍎'],
        'platano': ['🍌'],
        'naranja': ['🍊'],
        'limon': ['🍋'],
        'fresa': ['🍓'],
        'uva': ['🍇'],
        'sandia': ['🍉'],
        'piña': ['🍍'],
        'coco': ['🥥'],
        'kiwi': ['🥝'],
        'tomate': ['🍅'],
        'aguacate': ['🥑'],
        'brocoli': ['🥦'],
        'lechuga': ['🥬'],
        'pepino': ['🥒'],
        'pimiento': ['🌶️'],
        'maiz': ['🌽'],
        'zanahoria': ['🥕'],
        'ajo': ['🧄'],
        'cebolla': ['🧅'],
        'patata': ['🥔'],
        'batata': ['🍠'],
        'croissant': ['🥐'],
        'bagel': ['🥯'],
        'pan': ['🍞', '🥖', '🥨'],
        'queso': ['🧀'],
        'huevo': ['🥚'],
        'tocino': ['🥓'],
        'carne': ['🥩', '🍗', '🍖'],
        'perrito': ['🌭'],
        'patatas': ['🍟'],
        'bocadillo': ['🥪'],
        'burrito': ['🌯'],
        
        // Animales específicos
        'perro': ['🐶', '🐕', '🦮', '🐩'],
        'gato': ['🐱', '🐈'],
        'raton': ['🐭'],
        'hamster': ['🐹'],
        'conejo': ['🐰'],
        'zorro': ['🦊'],
        'oso': ['🐻', '🐼', '🐨'],
        'tigre': ['🐯'],
        'leon': ['🦁'],
        'vaca': ['🐮'],
        'cerdo': ['🐷'],
        'rana': ['🐸'],
        'mono': ['🐵', '🐒'],
        'pollo': ['🐔'],
        'pinguino': ['🐧'],
        'pajaro': ['🐦', '🐤', '🐣'],
        'pato': ['🦆'],
        'aguila': ['🦅'],
        'buho': ['🦉'],
        'murcielago': ['🦇'],
        'lobo': ['🐺'],
        'jabali': ['🐗'],
        'caballo': ['🐴'],
        'unicornio': ['🦄'],
        'abeja': ['🐝'],
        'mariposa': ['🦋'],
        'caracol': ['🐌'],
        'mariquita': ['🐞'],
        'hormiga': ['🐜'],
        'araña': ['🕷️', '🕸️'],
        'escorpion': ['🦂'],
        'tortuga': ['🐢'],
        'serpiente': ['🐍'],
        'lagarto': ['🦎'],
        'dinosaurio': ['🦖', '🦕'],
        'pulpo': ['🐙'],
        'calamar': ['🦑'],
        'gamba': ['🦐'],
        'langosta': ['🦞'],
        'cangrejo': ['🦀'],
        'pez': ['🐡', '🐠', '🐟'],
        'delfin': ['🐬'],
        'ballena': ['🐳', '🐋'],
        'tiburon': ['🦈'],
        'cocodrilo': ['🐊'],
        'leopardo': ['🐆'],
        'cebra': ['🦓'],
        'gorila': ['🦍'],
        'orangutan': ['🦧'],
        
        // Deportes específicos
        'futbol': ['⚽'],
        'baloncesto': ['🏀'],
        'rugby': ['🏈'],
        'beisbol': ['⚾'],
        'tenis': ['🎾'],
        'voleibol': ['🏐'],
        'futbol americano': ['🏉'],
        'billar': ['🎱'],
        'ping pong': ['🏓'],
        'badminton': ['🏸'],
        'natacion': ['🏊'],
        'carrera': ['🏃'],
        'trophy': ['🏆'],
        'medalla': ['🥇', '🥈', '🥉'],
        'diana': ['🎯'],
        'musculo': ['💪'],
        'fuego': ['🔥'],
        'rayo': ['⚡'],
        
        // Objetos específicos
        'telefono': ['📱', '📲'],
        'laptop': ['💻'],
        'teclado': ['⌨️'],
        'monitor': ['🖥️'],
        'impresora': ['🖨️'],
        'mouse': ['🖱️'],
        'trackball': ['🖲️'],
        'disco': ['💽', '💾', '💿', '📀'],
        'camara': ['📷', '📸', '📹', '📼'],
        'tv': ['📺'],
        'radio': ['📻'],
        'microfono': ['🎙️'],
        'control': ['🎚️', '🎛️'],
        'lupa': ['🔍', '🔎'],
        'vela': ['🕯️'],
        'bombilla': ['💡'],
        'linterna': ['🔦'],
        'lampara': ['🏮'],
        'diya': ['🪔'],
        'libro': ['📔', '📕', '📖', '📗', '📘', '📙', '📚', '📓', '📒'],
        'papel': ['📃', '📜', '📄', '📰', '🗞️', '📑'],
        'etiqueta': ['🔖', '🏷️'],
        'dinero': ['💰', '🪙', '💴', '💵', '💶', '💷'],
        'tarjeta': ['💳'],
        'recibo': ['🧾'],
        'diamante': ['💎'],
        'anillo': ['💍'],
        'flor': ['💐'],
        'regalo': ['🎁'],
        'confeti': ['🎉', '🎊'],
        'estrella': ['✨', '💫', '🌟'],
        'explosion': ['💥'],
        'enojo': ['💢'],
        'vapor': ['💨'],
        'agua': ['💧', '💦'],
        'caca': ['💩'],
        '100': ['💯'],
        
        // Corazones y amor
        'corazon': ['❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔', '❣️', '💕', '💞'],
        'rojo': ['❤️', '💔'],
        'naranja_corazon': ['🧡'],
        'amarillo': ['💛'],
        'verde': ['💚'],
        'azul': ['💙'],
        'morado': ['💜'],
        'negro': ['🖤'],
        'blanco': ['🤍'],
        'marron': ['🤎'],
        
        // Manos y gestos
        'manos': ['👊', '✌️', '🤝', '👋', '👌', '🤌', '🤏', '👈', '👉', '👆', '👇', '☝️', '👏'],
        'puño': ['👊'],
        'paz': ['✌️'],
        'apreton': ['🤝'],
        'ola': ['👋'],
        'ok': ['👌'],
        'dedos': ['🤌', '🤏'],
        'señalar': ['👈', '👉', '👆', '👇', '☝️'],
        'aplauso': ['👏'],
        'brazos': ['🙌', '👐', '🤲'],
        'oracion': ['🙏'],
        'escribir': ['✍️'],
        'pierna': ['🦵'],
        'pie': ['🦶'],
        'oreja': ['👂'],
        'sordo_oreja': ['🦻'],
        'nariz': ['👃'],
        'cerebro': ['🧠'],
        'organos': ['🫀', '🫁'],
        'dientes': ['🦷'],
        'hueso': ['🦴'],
        'ojos': ['👀', '👁️'],
        'lengua': ['👅'],
        'boca': ['👄'],
        'beso': ['💋'],
        'sangre': ['🩸'],
        'virus': ['🦠'],
        
        // Naturaleza
        'sol': ['🌞', '☀️', '🌤️', '⛅', '🌥️', '☁️'],
        'luna': ['🌙', '⭐', '🌟', '✨', '💫'],
        'nube': ['☁️', '🌦️', '🌧️', '⛈️', '🌩️'],
        'lluvia': ['🌧️', '☔'],
        'nieve': ['🌨️', '☃️', '⛄'],
        'viento': ['🌬️', '💨', '🌪️'],
        'niebla': ['🌫️'],
        'mar': ['🌊'],
        'gota': ['💧', '💦'],
        'paraguas': ['☂️', '🌂'],
        'montaña': ['🏔️', '⛰️', '🌋', '🗻'],
        'camping': ['🏕️'],
        'playa': ['🏖️'],
        'desierto': ['🏜️'],
        'isla': ['🏝️'],
        'parque': ['🏞️'],
        'estadio': ['🏟️'],
        'ruinas': ['🏛️'],
        'construccion': ['🏗️'],
        'ladrillo': ['🧱'],
        'roca': ['🪨'],
        'madera': ['🪵'],
        'cabaña': ['🛖'],
        'casa': ['🏘️', '🏚️', '🏠', '🏡'],
        'edificio': ['🏢', '🏣', '🏤', '🏥', '🏦', '🏨', '🏩', '🏪', '🏫', '🏬', '🏭', '🏯', '🏰'],
        
        // Música y entretenimiento
        'musica': ['🎮', '🕹️', '🎲', '🧩', '🎭', '🎪', '🎨', '🎬', '🎤', '🎧', '🎼', '🎹', '🥁'],
        'juego': ['🎮', '🕹️', '🎲', '🧩'],
        'teatro': ['🎭', '🎪'],
        'arte': ['🎨'],
        'cine': ['🎬'],
        'auriculares': ['🎧'],
        'partitura': ['🎼'],
        'piano': ['🎹'],
        'tambor': ['🥁'],
        'saxofon': ['🎷'],
        'trompeta': ['🎺'],
        'guitarra': ['🎸'],
        'violin': ['🎻'],
        'bolos': ['🎳'],
        
        // Transporte
        'coche': ['🚗', '🚕', '🚙', '🚌', '🚎', '🏎️', '🚓', '🚑', '🚒', '🚐', '🚚', '🚛', '🚜'],
        'taxi': ['🚕'],
        'camion': ['🚚', '🚛'],
        'tractor': ['🚜'],
        'moto': ['🛴', '🛵', '🏍️'],
        'policia': ['🚨', '🚔'],
        'ambulancia': ['🚑'],
        'bomberos': ['🚒'],
        'teleferico': ['🚡'],
        'funicular': ['🚠'],
        'tren': ['🚟', '🚃', '🚋', '🚞', '🚝', '🚄', '🚅', '🚈', '🚂', '🚆', '🚇', '🚊', '🚉'],
        'avion': ['✈️', '🛫', '🛬', '🛩️', '💺', '🛰️', '🚀', '🛸', '🚁', '🛶', '⛵', '🚤', '🛥️'],
        'satelite': ['🛰️'],
        'cohete': ['🚀'],
        'ovni': ['🛸'],
        'helicoptero': ['🚁'],
        'canoa': ['🛶'],
        'velero': ['⛵'],
        'lancha': ['🚤'],
        'yate': ['🛥️'],
        'barco': ['🛳️', '⛴️', '🚢'],
        'ancla': ['⚓'],
        'gasolina': ['⛽'],
        'parada': ['🚏'],
        'mapa': ['🗺️'],
        'moai': ['🗿'],
        'libertad': ['🗽'],
        'torre': ['🗼'],
        'castillo': ['🏰', '🏯']
      };
      
      for (const [keyword, emojiList] of Object.entries(keywords)) {
        if (keyword.includes(searchLower) && emojiList.includes(emoji)) {
          return true;
        }
      }
      
      return false;
    });
  };

  const globalSearchResults = searchAllEmojis();

  // Verificar si el usuario ya reaccionó con cada emoji
  const hasUserReacted = (emoji: string) => {
    if (!user || !reactions[emoji]) return false;
    return reactions[emoji].some(reaction => reaction.userId === user?.id);
  };

  // Contar cuántas reacciones tiene el usuario en este comentario
  const getUserReactionCount = () => {
    if (!user) return 0;
    let count = 0;
    Object.values(reactions).forEach(reactionList => {
      reactionList.forEach(reaction => {
        if (reaction.userId === user.id) {
          count++;
        }
      });
    });
    return count;
  };

  // Manejar reacción
  const handleReaction = async (emoji: string) => {
    if (!user || !token) {
      showNotification('Debes iniciar sesión para reaccionar', 'error');
      return;
    }

    try {
      const isRemoving = hasUserReacted(emoji);
      
      // Verificar límite de reacciones (solo al agregar)
      if (!isRemoving) {
        const userReactionCount = getUserReactionCount();
        if (userReactionCount >= 3) {
          showNotification('Ya tienes 3 reacciones en este comentario. Remueve una para agregar otra.', 'error');
          return;
        }
      }
      
      // Optimistic update - actualizar inmediatamente en la UI
      const updatedReactions = { ...reactions };
      
      if (isRemoving) {
        // Remover reacción
        if (updatedReactions[emoji]) {
          updatedReactions[emoji] = updatedReactions[emoji].filter(
            reaction => reaction.userId !== user?.id
          );
          if (updatedReactions[emoji].length === 0) {
            delete updatedReactions[emoji];
          }
        }
      } else {
        // Agregar reacción
        if (!updatedReactions[emoji]) {
          updatedReactions[emoji] = [];
        }
        updatedReactions[emoji].push({
          id: Date.now().toString(),
          userId: user?.id || '',
          userName: user?.name || 'Usuario',
          emoji,
          createdAt: new Date().toISOString()
        });
      }

      const newTotalReactions = Object.values(updatedReactions).reduce(
        (total, reactions) => total + reactions.length, 0
      );

      // Actualizar UI inmediatamente
      onReactionsUpdate(updatedReactions, newTotalReactions);
      setShowEmojiPicker(false);

      // Enviar por WebSocket para tiempo real
      if (isRemoving) {
        wsRemoveReaction(matchId, reviewId, emoji, user?.id || '');
      } else {
        wsAddReaction(matchId, reviewId, emoji, user?.id || '', user?.name || 'Usuario');
      }

      // También hacer la llamada HTTP para persistir en la BD
      const url = isRemoving 
        ? `${getApiUrl('/api')}/reviews/${reviewId}/reactions/${encodeURIComponent(emoji)}`
        : `${getApiUrl('/api')}/reviews/${reviewId}/reactions`;

      const response = await fetch(url, {
        method: isRemoving ? 'DELETE' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: isRemoving ? undefined : JSON.stringify({ emoji })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al procesar la reacción');
      }

      const message = isRemoving ? 'Reacción removida' : 'Reacción agregada';
      showNotification(message, 'success');

    } catch (error) {
      logger.error('Error handling reaction:', error);
      showNotification(error instanceof Error ? error.message : 'Error al procesar la reacción', 'error');
      
      // Revertir cambios si hay error
      // TODO: Implementar rollback si es necesario
    }
  };

  // Obtener emojis más populares para mostrar inline
  const getPopularEmojis = () => {
    const emojiCounts = Object.entries(reactions).map(([emoji, reactions]) => ({
      emoji,
      count: reactions.length
    }));
    
    return emojiCounts
      .sort((a, b) => b.count - a.count)
      .slice(0, 6) // Mostrar solo los 6 más populares
      .map(item => item.emoji);
  };

  const popularEmojis = getPopularEmojis();

  // Función para generar el contenido del tooltip
  const getTooltipContent = (emoji: string, reactionList: any[]) => {
    if (!reactionList || reactionList.length === 0) return '';
    
    const emojiDescriptions: { [key: string]: string } = {
      '😀': 'feliz',
      '😂': 'riendo',
      '😍': 'enamorado',
      '👍': 'pulgar arriba',
      '❤️': 'corazón',
      '🔥': 'fuego',
      '⚽': 'fútbol',
      '🏆': 'trofeo',
      '👏': 'aplauso',
      '🎉': 'celebrando',
      '😎': 'cool',
      '🤔': 'pensando',
      '😢': 'triste',
      '😡': 'enojado',
      '🤯': 'sorprendido',
      '🥰': 'amor',
      '😘': 'beso',
      '🤝': 'apretón de manos',
      '👊': 'puño',
      '✌️': 'paz',
      '🦽': 'silla de ruedas',
      '🧏': 'sordo',
      '👨‍🦯': 'ciego',
      '🌈': 'arcoíris',
      '🏳️‍🌈': 'bandera LGBT',
      '🍆': 'berenjena',
      '👴': 'anciano',
      '👵': 'anciana'
    };

    const description = emojiDescriptions[emoji] || emoji;
    
    if (reactionList.length === 1) {
      return `${reactionList[0].userName} reaccionó ${description}`;
    } else if (reactionList.length === 2) {
      return `${reactionList[0].userName} y ${reactionList[1].userName} reaccionaron ${description}`;
    } else {
      const others = reactionList.length - 2;
      return `${reactionList[0].userName}, ${reactionList[1].userName} y ${others} más reaccionaron ${description}`;
    }
  };

  // Función para mostrar tooltip
  const showTooltip = (event: React.MouseEvent | React.TouchEvent, emoji: string, reactionList: any[]) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const content = getTooltipContent(emoji, reactionList);
    
    if (content && reactionList && reactionList.length > 0) {
      setTooltipData({
        show: true,
        x: rect.left + rect.width / 2,
        y: rect.top - 10,
        content
      });
    } else {
      hideTooltip();
    }
  };

  // Función para ocultar tooltip
  const hideTooltip = () => {
    setTooltipData(null);
  };



  return (
    <div style={{ position: 'relative' }}>
      {/* Reacciones existentes - inline */}
      <div style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '8px',
        flexWrap: 'wrap',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'flex-start' : 'center'
      }}>
        {/* Emojis con contadores */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          flexWrap: 'wrap'
        }}>
          {Object.entries(reactions).map(([emoji, reactionList]) => (
            <button
              key={emoji}
              onClick={() => handleReaction(emoji)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 8px',
                backgroundColor: hasUserReacted(emoji) ? theme.colors.primary + '20' : 'transparent',
                border: `1px solid ${hasUserReacted(emoji) ? theme.colors.primary : theme.colors.border}`,
                borderRadius: '16px',
                cursor: 'pointer',
                fontSize: '14px',
                fontFamily: theme.fonts.body,
                transition: 'all 0.2s ease',
                minWidth: '32px',
                justifyContent: 'center'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.05)';
                e.currentTarget.style.backgroundColor = hasUserReacted(emoji) 
                  ? theme.colors.primary + '30' 
                  : theme.colors.background;
                showTooltip(e, emoji, reactionList);
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.backgroundColor = hasUserReacted(emoji) 
                  ? theme.colors.primary + '20' 
                  : 'transparent';
                hideTooltip();
              }}
              onTouchStart={(e) => {
                // Para mobile: mostrar tooltip después de un delay
                setTimeout(() => {
                  showTooltip(e, emoji, reactionList);
                }, 500);
              }}
              onTouchEnd={() => {
                // Para mobile: ocultar tooltip
                setTimeout(() => {
                  hideTooltip();
                }, 2000);
              }}
            >
              <span style={{ fontSize: '16px' }}>{emoji}</span>
              {reactionList.length > 1 && (
                <span style={{
                  fontSize: '12px',
                  fontWeight: 'bold',
                  color: hasUserReacted(emoji) ? theme.colors.primary : theme.colors.textSecondary
                }}>
                  {reactionList.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Botón de reaccionar - consistente en mobile y desktop */}
        <button
          onClick={() => setShowEmojiPicker(!showEmojiPicker)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 12px',
            backgroundColor: showEmojiPicker ? theme.colors.primary : 'transparent',
            color: showEmojiPicker ? theme.colors.white : theme.colors.textSecondary,
            border: `1px solid ${showEmojiPicker ? theme.colors.primary : theme.colors.border}`,
            borderRadius: '16px',
            cursor: 'pointer',
            fontSize: '14px',
            fontFamily: theme.fonts.body,
            transition: 'all 0.2s ease',
            marginTop: isMobile ? '8px' : '0px',
            alignSelf: isMobile ? 'flex-start' : 'center'
          }}
        >
          <span style={{ fontSize: '16px' }}>😀</span>
          <span>Reaccionar</span>
        </button>
      </div>

      {/* Picker de emojis completo */}
      {showEmojiPicker && (
        <div style={{
          position: isMobile ? 'fixed' : 'absolute',
          bottom: isMobile ? '20px' : '100%',
          left: isMobile ? '20px' : '0',
          right: isMobile ? '20px' : 'auto',
          backgroundColor: theme.colors.surface,
          border: `1px solid ${theme.colors.border}`,
          borderRadius: theme.borderRadius.card,
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
          zIndex: 1000,
          minWidth: isMobile ? 'auto' : '320px',
          maxWidth: isMobile ? 'none' : '400px',
          padding: theme.spacing.md,
          marginBottom: '8px',
          maxHeight: isMobile ? '60vh' : '400px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: theme.spacing.sm,
            paddingBottom: theme.spacing.sm,
            borderBottom: `1px solid ${theme.colors.border}`
          }}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '4px'
            }}>
              <h4 style={{
                margin: 0,
                fontSize: '14px',
                fontWeight: 'bold',
                color: theme.colors.text,
                fontFamily: theme.fonts.body
              }}>
                Reacciones
              </h4>
              {user && (
                <div style={{
                  fontSize: '11px',
                  color: getUserReactionCount() >= 3 ? theme.colors.danger : theme.colors.textSecondary,
                  fontFamily: theme.fonts.body
                }}>
                  {getUserReactionCount()}/3 reacciones usadas
                </div>
              )}
            </div>
            <button
              onClick={() => setShowEmojiPicker(false)}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '18px',
                cursor: 'pointer',
                color: theme.colors.textSecondary,
                padding: '4px'
              }}
            >
              ✕
            </button>
          </div>

          {/* Buscador */}
          <div style={{
            marginBottom: theme.spacing.sm
          }}>
            <input
              type="text"
              placeholder="Buscar emoji..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: `1px solid ${theme.colors.border}`,
                borderRadius: theme.borderRadius.input,
                fontSize: '14px',
                fontFamily: theme.fonts.body,
                backgroundColor: theme.colors.background,
                color: theme.colors.text
              }}
            />
          </div>

          {/* Resultados de búsqueda global */}
          {searchTerm && globalSearchResults.length > 0 && (
            <div style={{
              marginBottom: theme.spacing.sm,
              paddingBottom: theme.spacing.sm,
              borderBottom: `1px solid ${theme.colors.border}`
            }}>
              <div style={{
                fontSize: '12px',
                color: theme.colors.textSecondary,
                marginBottom: '8px'
              }}>
                Resultados de búsqueda ({globalSearchResults.length})
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(8, 1fr)',
                gap: '4px',
                maxHeight: '120px',
                overflowY: 'auto'
              }}>
                {globalSearchResults.slice(0, 32).map((emoji: string) => (
                  <button
                    key={emoji}
                    onClick={() => handleReaction(emoji)}
                    style={{
                      padding: '8px',
                      backgroundColor: hasUserReacted(emoji) ? theme.colors.primary + '20' : theme.colors.background,
                      border: `1px solid ${hasUserReacted(emoji) ? theme.colors.primary : theme.colors.border}`,
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '20px',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: '40px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'scale(1.1)';
                      e.currentTarget.style.backgroundColor = hasUserReacted(emoji) 
                        ? theme.colors.primary + '30' 
                        : theme.colors.surface;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'scale(1)';
                      e.currentTarget.style.backgroundColor = hasUserReacted(emoji) 
                        ? theme.colors.primary + '20' 
                        : theme.colors.background;
                    }}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          )}



          {/* Categorías con scroll horizontal */}
          {!searchTerm && (
            <div style={{
              marginBottom: theme.spacing.sm,
              paddingBottom: '6px',
              borderBottom: `1px solid ${theme.colors.border}`
            }}>
              <div style={{
                fontSize: '11px',
                color: theme.colors.textSecondary,
                marginBottom: '6px',
                fontWeight: 'bold'
              }}>
                Categorías:
              </div>
              <div style={{
                display: 'flex',
                gap: '4px',
                overflowX: 'auto',
                paddingBottom: '4px',
                scrollbarWidth: 'thin',
                scrollbarColor: `${theme.colors.border} transparent`
              }}>
                {Object.keys(WHATSAPP_EMOJIS).map(category => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: selectedCategory === category ? theme.colors.primary : theme.colors.background,
                      color: selectedCategory === category ? theme.colors.white : theme.colors.textSecondary,
                      border: `1px solid ${selectedCategory === category ? theme.colors.primary : theme.colors.border}`,
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '10px',
                      fontFamily: theme.fonts.body,
                      whiteSpace: 'nowrap',
                      transition: 'all 0.2s ease',
                      textAlign: 'center',
                      fontWeight: selectedCategory === category ? 'bold' : 'normal',
                      minHeight: '28px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                    onMouseEnter={(e) => {
                      if (selectedCategory !== category) {
                        e.currentTarget.style.backgroundColor = theme.colors.surface;
                        e.currentTarget.style.transform = 'scale(1.02)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedCategory !== category) {
                        e.currentTarget.style.backgroundColor = theme.colors.background;
                        e.currentTarget.style.transform = 'scale(1)';
                      }
                    }}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Grid de emojis de la categoría seleccionada */}
          {!searchTerm && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(8, 1fr)',
              gap: '4px',
              flex: 1,
              overflowY: 'auto',
              padding: '4px'
            }}>
              {currentEmojis.map((emoji: string) => (
                <button
                  key={emoji}
                  onClick={() => handleReaction(emoji)}
                  style={{
                    padding: '8px',
                    backgroundColor: hasUserReacted(emoji) ? theme.colors.primary + '20' : theme.colors.background,
                    border: `1px solid ${hasUserReacted(emoji) ? theme.colors.primary : theme.colors.border}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '20px',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '40px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.1)';
                    e.currentTarget.style.backgroundColor = hasUserReacted(emoji) 
                      ? theme.colors.primary + '30' 
                      : theme.colors.surface;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.backgroundColor = hasUserReacted(emoji) 
                      ? theme.colors.primary + '20' 
                      : theme.colors.background;
                  }}
                >
                  {emoji}
                </button>
              ))}
            </div>
          )}

          {/* Mensaje cuando no hay resultados de búsqueda */}
          {searchTerm && globalSearchResults.length === 0 && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px',
              color: theme.colors.textSecondary,
              fontSize: '14px',
              fontFamily: theme.fonts.body
            }}>
              No se encontraron emojis para "{searchTerm}"
            </div>
          )}
        </div>
      )}

      {/* Tooltip para mostrar quién reaccionó */}
      {tooltipData && (
        <div style={{
          position: 'fixed',
          left: tooltipData.x,
          top: tooltipData.y,
          transform: 'translateX(-50%)',
          backgroundColor: theme.colors.surface,
          border: `1px solid ${theme.colors.border}`,
          borderRadius: '8px',
          padding: '8px 12px',
          fontSize: '12px',
          fontFamily: theme.fonts.body,
          color: theme.colors.text,
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          zIndex: 10000,
          pointerEvents: 'none',
          whiteSpace: 'nowrap',
          maxWidth: '300px'
        }}>
          {tooltipData.content}
        </div>
      )}
    </div>
  );
}; 